import { iMail } from '@hover/icons';
import React, { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import * as yup from 'yup';

import { TextField } from 'src/shared/components/forms';
import { useAuthenticationMethod, useTranslate } from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';

const emailSchema = yup.string().required().email();

interface EmailFormData {
  email: string;
}

export interface EmailAuthenticationInputProps {
  checkSingleSignOn: boolean;
  checkBeacon: boolean;
}

/**
 * A special email input that automatically makes requests to determine the
 * appropriate authentication method for the provided email. Current
 * authentication methods include:
 *
 * - Single Sign-On (SSO)
 * - Beacon Pro+
 */
const EmailAuthenticationInput = ({
  checkBeacon: beacon,
  checkSingleSignOn: singleSignOn,
}: EmailAuthenticationInputProps) => {
  const t = useTranslate(TranslationNamespace.LOGIN);
  const { checkEmail, activeMethod } = useAuthenticationMethod();
  const fieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (activeMethod === 'password') return;

    // If the native browser autocomplete dropdown is still open when we
    // transition from password to email-based login, it will obscure the email
    // input since the input transitions downward. This simply forces the menu
    // closed without disabling `autocomplete`.

    const el = fieldRef.current;

    if (el) {
      el.autocomplete = 'off';
      el.autocomplete = 'on';
    }
  }, [activeMethod]);

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { watch } = useFormContext<EmailFormData>();

  const emailPropName = 'email';
  const email = watch(emailPropName);
  const isEmailValid = emailSchema.isValidSync(email);

  useEffect(() => {
    if (activeMethod === 'password' && isEmailValid && checkEmail) {
      void checkEmail(email, { beacon, singleSignOn });
    }
  }, [activeMethod, beacon, checkEmail, email, isEmailValid, singleSignOn]);

  return (
    <TextField
      iconBefore={iMail}
      id="email"
      label={t('email')}
      name={emailPropName}
      placeholder={t('email')}
      ref={fieldRef}
      type="email"
    />
  );
};

export { EmailAuthenticationInput };
